import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Typography, message } from "antd";
import VideoReviewPoster from "./VideoReviewPoster";
import copyToClipboard from "../../utils/copy";
import {
    downloadVideoReviewPosterFromElement,
    ensurePosterFontsLoaded,
} from "../../utils/videoReviewPoster";
import { useTranslation } from "../../i18n";

const { Paragraph } = Typography;

function VideoReviewSharePanel({ url, videoName, expiresAtLabel = "", compact = false }) {
    const { t } = useTranslation();
    const posterRef = useRef(null);
    const [posterReady, setPosterReady] = useState(false);
    const [posterError, setPosterError] = useState("");

    useEffect(() => {
        if (!url) {
            setPosterReady(false);
            setPosterError("");
            return;
        }

        setPosterReady(false);
        setPosterError("");

        let cancelled = false;

        async function preparePoster() {
            try {
                await ensurePosterFontsLoaded();
                if (!cancelled) {
                    setPosterReady(true);
                }
            } catch (error) {
                console.error("Video review poster font loading failed:", error);
                if (!cancelled) {
                    setPosterReady(true);
                }
            }
        }

        preparePoster();

        return () => {
            cancelled = true;
        };
    }, [url, videoName, expiresAtLabel]);

    const handleCopy = useCallback(async () => {
        if (!url) {
            return;
        }
        try {
            await copyToClipboard(url);
            message.success(t("videoReview.share.linkCopied"));
        } catch {
            message.error(t("videoReview.share.copyFailed"));
        }
    }, [url, t]);

    const handleDownloadPoster = useCallback(async () => {
        if (!url || !posterRef.current) {
            return;
        }
        try {
            await downloadVideoReviewPosterFromElement(posterRef.current, videoName);
            message.success(t("videoReview.share.posterDownloaded"));
        } catch (error) {
            console.error("Video review poster download failed:", error);
            setPosterError(t("videoReview.share.posterFailed"));
            message.error(t("videoReview.share.posterDownloadFailed"));
        }
    }, [url, videoName, t]);

    return (
        <div className={`video-review-share${compact ? " video-review-share--compact" : ""}`}>
            {!compact && (
                <div className="video-review-share__header">
                    <h2 className="video-review-share__video-name">{videoName}</h2>
                    <p className="video-review-share__subtitle">{t("videoReview.share.subtitle")}</p>
                    <p className="video-review-share__subtitle-en">{t("videoReview.share.subtitleEn")}</p>
                </div>
            )}

            <div className="video-review-share__url-row">
                <Input readOnly value={url} aria-label={t("videoReview.share.urlLabel")} />
                <Button type="primary" onClick={handleCopy}>
                    {t("videoReview.share.copyLink")}
                </Button>
            </div>

            <div className="video-review-share__actions">
                <Button
                    onClick={handleDownloadPoster}
                    disabled={!posterReady || Boolean(posterError)}
                >
                    {t("videoReview.share.downloadPoster")}
                </Button>
            </div>

            <div className="video-review-share__poster-wrap">
                {!posterReady && url && (
                    <p className="video-review-share__poster-status">
                        {t("videoReview.share.posterLoading")}
                    </p>
                )}
                {posterError && (
                    <Paragraph type="danger" className="video-review-share__poster-status">
                        {posterError}
                    </Paragraph>
                )}
                {url && (
                    <div className={posterReady ? "" : "video-review-share__poster--loading"}>
                        <VideoReviewPoster
                            ref={posterRef}
                            url={url}
                            videoName={videoName}
                            expiresAtLabel={expiresAtLabel}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default VideoReviewSharePanel;
